export interface ICalculateAdjustedPricesParams {
  min: number | null;
  max: number | null;
  isMinChanged: boolean;
  pricesMinGap: number;
}

export interface ICalculateAdjustedPricesResult {
  adjustedMin: number | null;
  adjustedMax: number | null;
  needsAdjustment: boolean;
}

/**
 * Рассчитывает скорректированные цены с учетом минимального разбега.
 *
 * Правила коррекции:
 *
 * При изменении min (isMinChanged = true):
 * - Если min или max null, коррекция не нужна
 * - Если разбег (max - min) < pricesMinGap, подстраиваем max = min + pricesMinGap
 * - Если разбег >= pricesMinGap, коррекция не нужна
 *
 * При изменении max (isMinChanged = false):
 * - Если max null, коррекция не нужна
 * - Если max <= pricesMinGap, обнуляем min и устанавливаем max = pricesMinGap
 * - Если разбег (max - (min || 0)) < pricesMinGap, подстраиваем min = max - pricesMinGap
 * - Если разбег >= pricesMinGap, коррекция не нужна
 */
export function calculateAdjustedPrices({
  min,
  max,
  isMinChanged,
  pricesMinGap,
}: ICalculateAdjustedPricesParams): ICalculateAdjustedPricesResult {
  const adjustedMin = min;
  let adjustedMax = max;
  let needsAdjustment = false;

  // Обработка случая, когда изменили min
  if (isMinChanged) {
    // Если min или max null, коррекция не нужна
    if (min === null || max === null) {
      return { adjustedMin: min, adjustedMax: max, needsAdjustment: false };
    }

    // Если разбег меньше минимального, подстраиваем max
    const gap = max - min;
    if (gap < pricesMinGap) {
      adjustedMax = min + pricesMinGap;
      needsAdjustment = true;
    }

    // Обработка случая, когда изменили max
  } else {
    // Если max null, коррекция не нужна
    if (max === null) {
      return { adjustedMin: min, adjustedMax: max, needsAdjustment: false };
    }

    // Если max слишком мал, обнуляем min и устанавливаем max равным pricesMinGap
    if (max < pricesMinGap) {
      return { adjustedMin: null, adjustedMax: pricesMinGap, needsAdjustment: true };
    }

    // Если разбег меньше минимального, подстраиваем min (используем min || 0 для null)
    const gap = max - (min || 0);
    if (gap < pricesMinGap) {
      return { adjustedMin: max - pricesMinGap, adjustedMax: max, needsAdjustment: true };
    }
  }

  return { adjustedMin, adjustedMax, needsAdjustment };
}
